import { useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Settings,
  ChevronLeft,
  LogOut,
  CalendarDays,
  Cake,
  Wallet,
  Gift,
  X,
  Shield,
  Network,
  Gamepad2,
  Vote,
  Image,
  Beer,
  Send,
  Lightbulb,
  Hash,
  PlaySquare,
  Film,
} from 'lucide-react';

import { ROUTES } from '@/utils/constants';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';
import DonateSidebarButton from '@/components/donate/DonateSidebarButton';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchBirthdaysThisWeek,
  selectBirthdaysThisWeek,
} from '@/store/birthdaySlice';
import {
  fetchMyBalance,
  selectMyBalance,
} from '@/store/expenseSlice';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: number | string;
  badgeColor?: 'primary' | 'pink' | 'green' | 'red';
}

function Sidebar({ collapsed, onToggle, isMobileOpen = false, onMobileClose }: SidebarProps): React.ReactElement {
  const { user, handleLogout } = useAuth();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const birthdaysThisWeek = useAppSelector(selectBirthdaysThisWeek);
  const myBalance = useAppSelector(selectMyBalance);

  // Fetch birthdays this week for badge
  useEffect(() => {
    dispatch(fetchBirthdaysThisWeek());
    dispatch(fetchMyBalance());
  }, [dispatch]);

  // Close mobile menu on route change - must run BEFORE navigation happens
  useEffect(() => {
    // Immediately close the mobile menu when route changes
    if (onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname, onMobileClose]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      // Force GPU layer to prevent rendering artifacts
      document.body.style.transform = 'translateZ(0)';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.transform = '';
      // Also reset any pointer-events that might have been set
      document.body.style.pointerEvents = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.transform = '';
      document.body.style.pointerEvents = '';
    };
  }, [isMobileOpen]);

  // Close mobile menu when clicking a link
  const handleNavClick = useCallback(() => {
    if (isMobileOpen && onMobileClose) {
      // Immediate close for better UX
      onMobileClose();
    }
  }, [isMobileOpen, onMobileClose]);

  // Calculate expense badge
  const getExpenseBadge = (): { value: string | undefined; color: 'green' | 'red' | undefined } => {
    if (!myBalance) return { value: undefined, color: undefined };

    const netBalance = myBalance.netBalance;
    if (netBalance === 0) return { value: undefined, color: undefined };

    // Format the balance for the badge
    const absValue = Math.abs(netBalance);
    let badgeText: string;

    if (absValue >= 1000) {
      badgeText = `${(absValue / 1000).toFixed(1)}k`;
    } else if (absValue >= 100) {
      badgeText = `${Math.round(absValue)}`;
    } else {
      badgeText = absValue.toFixed(0);
    }

    return {
      value: netBalance > 0 ? `+${badgeText}` : `-${badgeText}`,
      color: netBalance > 0 ? 'green' : 'red',
    };
  };

  const expenseBadge = getExpenseBadge();

  const mainNavItems: NavItem[] = [
    { label: 'Dashboard', icon: LayoutDashboard, path: ROUTES.DASHBOARD },
    { label: 'Events', icon: CalendarDays, path: ROUTES.EVENTS },
    { label: 'Geburtstage', icon: Cake, path: ROUTES.BIRTHDAYS, badge: birthdaysThisWeek.length > 0 ? birthdaysThisWeek.length : undefined, badgeColor: 'pink' },
    { label: 'GroupPay', icon: Wallet, path: ROUTES.EXPENSES, badge: expenseBadge.value, badgeColor: expenseBadge.color },
    { label: 'Überweisungen', icon: Send, path: ROUTES.TRANSFERS },
    { label: 'Geschenke', icon: Gift, path: ROUTES.GIFTS },
    { label: 'Umfragen', icon: Vote, path: '/polls' },
    { label: 'Feature-Ideen', icon: Lightbulb, path: ROUTES.FEATURE_REQUESTS },
    { label: 'Media Gallery', icon: Image, path: '/media' },
    { label: 'Watchlist', icon: PlaySquare, path: ROUTES.WATCHLIST },
    { label: 'Serien & Filme', icon: Film, path: ROUTES.MEDIA_RECOMMENDATIONS },
    { label: 'Verkehrsindex', icon: Network, path: ROUTES.TRAFFIC_INDEX },
  ];

  const gameNavItems: NavItem[] = [
    { label: 'Klobi-Jump', icon: Gamepad2, path: '/klobi-jump' },
    { label: 'Hangman', icon: Hash, path: '/hangman' },
    { label: 'Trinkspiele', icon: Beer, path: '/drinking-games' },
  ];

  const bottomNavItems: NavItem[] = [
    { label: 'Settings', icon: Settings, path: ROUTES.SETTINGS },
  ];

  // Admin-only navigation items - show if user is admin
  const adminNavItems: NavItem[] = user?.role === 'admin' ? [
    { label: 'Admin Dashboard', icon: Shield, path: '/admin' },
  ] : [];

  // Game Master-only navigation items - show ONLY if user is Game Master (not admin)
  const gameMasterNavItems: NavItem[] = user?.isHangmanGameMaster ? [
    { label: 'Hangman Wörter', icon: Hash, path: '/admin/hangman' },
  ] : [];

  return (
    <>
      {/* Mobile overlay backdrop - using CSS transitions instead of AnimatePresence for reliability */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-background-darkest/85 lg:hidden transition-opacity duration-150',
          isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onMobileClose}
        onTouchEnd={onMobileClose}
        aria-hidden={!isMobileOpen}
      />

      {/* Sidebar */}
      <aside
        className={cn(
          'h-screen bg-background-card border-r border-border transition-all duration-300',
          // Mobile: fixed overlay with transform animation
          'fixed left-0 top-0 z-50 w-72',
          'transform transition-transform duration-300 ease-out',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: static position in document flow, always visible
          'lg:static lg:z-auto lg:transform-none lg:translate-x-0',
          collapsed ? 'lg:w-16' : 'lg:w-64',
          'safe-area-inset-top safe-area-inset-bottom'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-14 sm:h-16 px-4 border-b border-border">
            <AnimatePresence mode="wait">
              {(!collapsed || isMobileOpen) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <h1 className="text-xl font-bold text-gradient-primary">BOIS</h1>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Desktop: Toggle button */}
            <button
              onClick={onToggle}
              className={cn(
                'p-2 rounded-lg hover:bg-background-elevated transition-colors hidden lg:block',
                collapsed && 'mx-auto'
              )}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft
                className={cn(
                  'h-5 w-5 text-foreground-muted transition-transform duration-300',
                  collapsed && 'rotate-180'
                )}
              />
            </button>

            {/* Mobile: Close button with larger touch target */}
            {isMobileOpen && (
              <button
                onClick={onMobileClose}
                className="p-2.5 -mr-1 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors lg:hidden touch-manipulation"
                aria-label="Close menu"
              >
                <X className="h-6 w-6 text-foreground-muted" />
              </button>
            )}
          </div>

        {/* Main Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto no-scrollbar overscroll-contain">
          {mainNavItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={collapsed} isMobile={isMobileOpen} onNavClick={handleNavClick} />
          ))}

          {/* Games Section */}
          <div className={cn(
            'mt-4 mb-2 px-3 text-xs font-semibold text-foreground-muted uppercase',
            (!isMobileOpen && collapsed) && 'text-center'
          )}>
            {(!isMobileOpen && collapsed) ? '🎮' : 'Games'}
          </div>
          {gameNavItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={collapsed} isMobile={isMobileOpen} onNavClick={handleNavClick} />
          ))}

          {/* Admin Section */}
          {adminNavItems.length > 0 && (
            <>
              <div className={cn(
                'mt-4 mb-2 px-3 text-xs font-semibold text-foreground-muted uppercase',
                (!isMobileOpen && collapsed) && 'text-center'
              )}>
                {(!isMobileOpen && collapsed) ? '—' : 'Admin'}
              </div>
              {adminNavItems.map((item) => (
                <NavItem key={item.path} item={item} collapsed={collapsed} isMobile={isMobileOpen} onNavClick={handleNavClick} />
              ))}
            </>
          )}

          {/* Game Master Section */}
          {gameMasterNavItems.length > 0 && (
            <>
              <div className={cn(
                'mt-4 mb-2 px-3 text-xs font-semibold text-foreground-muted uppercase',
                (!isMobileOpen && collapsed) && 'text-center'
              )}>
                {(!isMobileOpen && collapsed) ? '🎯' : 'Game Master'}
              </div>
              {gameMasterNavItems.map((item) => (
                <NavItem key={item.path} item={item} collapsed={collapsed} isMobile={isMobileOpen} onNavClick={handleNavClick} />
              ))}
            </>
          )}
        </nav>

        {/* Bottom Navigation */}
        <div className="px-2 py-4 space-y-1 border-t border-border">
          {bottomNavItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={collapsed} isMobile={isMobileOpen} onNavClick={handleNavClick} />
          ))}

          {/* Fun Donate Button */}
          <div className="py-1">
            <DonateSidebarButton collapsed={collapsed} isMobileOpen={isMobileOpen} />
          </div>

          {/* Logout button with better mobile touch target */}
          <button
            onClick={handleLogout}
            className={cn(
              'flex items-center w-full px-3 py-2.5 lg:py-2 rounded-lg text-foreground-muted',
              'hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20 transition-colors touch-manipulation',
              !isMobileOpen && collapsed && 'justify-center'
            )}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <AnimatePresence mode="wait">
              {(isMobileOpen || !collapsed) && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.15 }}
                  className="ml-3 whitespace-nowrap overflow-hidden"
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>

        {/* User Profile Section */}
        <div className="px-2 py-4 border-t border-border">
          <NavLink
            to={ROUTES.PROFILE}
            onClick={handleNavClick}
            className={cn(
              'flex items-center px-3 py-2.5 lg:py-2 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors touch-manipulation',
              !isMobileOpen && collapsed && 'justify-center'
            )}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-background">
                {user?.profile?.displayName?.charAt(0) ?? user?.profile?.username?.charAt(0) ?? 'U'}
              </span>
            </div>
            <AnimatePresence mode="wait">
              {(isMobileOpen || !collapsed) && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.15 }}
                  className="ml-3 overflow-hidden"
                >
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.profile?.displayName ?? user?.profile?.username ?? 'User'}
                  </p>
                  <p className="text-xs text-foreground-subtle truncate">
                    {user?.email ?? ''}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </NavLink>
        </div>
      </div>
    </aside>
    </>
  );
}

interface NavItemProps {
  item: NavItem;
  collapsed: boolean;
  isMobile?: boolean;
  onNavClick?: () => void;
}

function NavItem({ item, collapsed, isMobile, onNavClick }: NavItemProps): React.ReactElement {
  const Icon = item.icon;
  const isBirthdayItem = item.path === ROUTES.BIRTHDAYS;
  const isExpenseItem = item.path === ROUTES.EXPENSES;

  // Determine colors based on item type and badge color
  const getItemColors = (isActive: boolean) => {
    if (isBirthdayItem) {
      return isActive ? 'bg-pink-500/10 text-pink-400' : '';
    }
    if (isExpenseItem) {
      return isActive ? 'bg-emerald-500/10 text-emerald-400' : '';
    }
    return isActive ? 'bg-primary/10 text-primary' : '';
  };

  const getIndicatorColor = () => {
    if (isBirthdayItem) return 'bg-pink-400';
    if (isExpenseItem) return 'bg-emerald-400';
    return 'bg-primary';
  };

  const getBadgeColors = () => {
    if (item.badgeColor === 'pink') {
      return { bg: 'bg-pink-500/20', text: 'text-pink-400', solid: 'bg-pink-500 text-white' };
    }
    if (item.badgeColor === 'green') {
      return { bg: 'bg-green-500/20', text: 'text-green-400', solid: 'bg-green-500 text-white' };
    }
    if (item.badgeColor === 'red') {
      return { bg: 'bg-red-500/20', text: 'text-red-400', solid: 'bg-red-500 text-white' };
    }
    return { bg: 'bg-primary/20', text: 'text-primary', solid: 'bg-primary text-background' };
  };

  const badgeColors = getBadgeColors();

  // For mobile: always show expanded content
  const showExpanded = isMobile || !collapsed;

  return (
    <NavLink
      to={item.path}
      onClick={onNavClick}
      className={({ isActive }) =>
        cn(
          'flex items-center px-3 py-2.5 lg:py-2 rounded-lg transition-colors relative group touch-manipulation',
          isActive
            ? getItemColors(true)
            : 'text-foreground-muted hover:bg-background-elevated hover:text-foreground active:bg-background-muted',
          !showExpanded && 'justify-center'
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active indicator */}
          {isActive && (
            <motion.div
              layoutId="activeIndicator"
              className={cn(
                'absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full',
                getIndicatorColor()
              )}
            />
          )}

          <Icon className={cn(
            'h-5 w-5 flex-shrink-0',
            isBirthdayItem && isActive && 'text-pink-400',
            isExpenseItem && isActive && 'text-emerald-400'
          )} />

          <AnimatePresence mode="wait">
            {showExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.15 }}
                className="ml-3 whitespace-nowrap overflow-hidden"
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Badge */}
          {item.badge && showExpanded && (
            <span className={cn(
              'ml-auto px-2 py-0.5 text-xs font-medium rounded-full',
              badgeColors.bg,
              badgeColors.text
            )}>
              {item.badge}
            </span>
          )}

          {/* Badge for collapsed state */}
          {item.badge && !showExpanded && (
            <span className={cn(
              'absolute -top-1 -right-1 min-w-4 h-4 px-1 text-[10px] font-medium rounded-full flex items-center justify-center',
              badgeColors.solid
            )}>
              {typeof item.badge === 'number' && item.badge > 9 ? '9+' : item.badge}
            </span>
          )}

          {/* Tooltip for collapsed state */}
          {!showExpanded && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-background-elevated text-foreground text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
              {item.label}
              {item.badge && (
                <span className={cn(
                  'ml-2 px-1.5 py-0.5 text-xs rounded-full',
                  badgeColors.bg,
                  badgeColors.text
                )}>
                  {item.badge}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </NavLink>
  );
}

export default Sidebar;
